import authService from './authService';
import config from '../config';

const LEGAL_API_PREFIX = '/api/v1/legal';

/**
 * Fetches the list of active legal acknowledgements for a specific site.
 * @param {string} siteId - The unique identifier for the application (e.g., 'workout_smart').
 * @returns {Promise<Array>} Array of legal document objects.
 */
export const getActiveAcknowledgements = async (siteId) => {
  const url = `${config.API_BASE_URL}${LEGAL_API_PREFIX}/acknowledgements?site_id=${siteId}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch legal documents: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {}
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[legalApi] Error fetching acknowledgements:', error);
    throw error;
  }
};

/**
 * Checks the user's compliance status for legal documents.
 * @param {string} siteId - The unique identifier for the application.
 * @returns {Promise<Object>} UserComplianceStatusResponse
 */
export const getLegalComplianceStatus = async (siteId) => {
  // CHANGED: Added timestamp to prevent caching of the status check
  const url = `${config.API_BASE_URL}${LEGAL_API_PREFIX}/status?site_id=${siteId}&t=${Date.now()}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch legal status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {}
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[legalApi] Error fetching legal status:', error);
    throw error;
  }
};

/**
 * Records user consent for a specific legal document.
 * @param {string} documentId - The ID of the document being agreed to.
 * @param {string} [commitHash] - Optional frontend commit hash for auditing.
 * @returns {Promise<Object>} The consent confirmation record.
 */
export const recordUserConsent = async (documentId, commitHash = null) => {
  const url = `${config.API_BASE_URL}${LEGAL_API_PREFIX}/consent`;
  const payload = {
    document_id: documentId,
    frontend_commit_hash: commitHash || process.env.REACT_APP_COMMIT_HASH || 'unknown',
  };

  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });

    if (!response.ok) {
      let errorDetail = `Failed to record consent: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {}
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[legalApi] Error recording consent:', error);
    throw error;
  }
};

/**
 * Publishes a new legal document version (Admin only).
 * @param {Object} documentData - The document data.
 * @returns {Promise<Object>} The created document record.
 */
export const publishDocument = async (documentData) => {
  const url = `${config.API_BASE_URL}${LEGAL_API_PREFIX}/documents`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: documentData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to publish document: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {}
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[legalApi] Error publishing document:', error);
    throw error;
  }
};

/**
 * Deactivates an existing legal document (Admin only).
 * @param {string} documentId - The ID of the document to deactivate.
 * @returns {Promise<boolean>} True if successful.
 */
export const deactivateDocument = async (documentId) => {
  const url = `${config.API_BASE_URL}${LEGAL_API_PREFIX}/documents/${documentId}/deactivate`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
    });
    if (!response.ok) {
      let errorDetail = `Failed to deactivate document: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {}
      throw new Error(errorDetail);
    }
    return true;
  } catch (error) {
    console.error('[legalApi] Error deactivating document:', error);
    throw error;
  }
};

export default {
  getActiveAcknowledgements,
  getLegalComplianceStatus,
  recordUserConsent,
  publishDocument,
  deactivateDocument,
};